import React, { useState, useRef, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import axios from "axios";
import { SERVICE_LOCATION, GET_MATERIAL_MANUFACTURER_REPORT } from "./../constants/endpoints";

import "./../assets/styles/table.css";
import "./../assets/styles/fitter.css";
import "./../assets/styles/reports.css";

const MaterialManufacturerReport = (props) => {
    const { data } = props;
    const userDepot = (data !== undefined && data !== null) ? data.data.userDetails.depot : "";

    const toast = useRef(null);

    const [materialManufacturerReportData, setMaterialManufacturerReportData] = useState(null);
    const [filteredReportData, setFilteredReportData] = useState(null);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [minDate, setMinDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [disableSubmitButton, setDisableSubmitButton] = useState(true);
    const [showReport, setShowReport] = useState(false);

    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState("");

    useEffect(() => {
        initFilters();
    }, []);

    const getFormattedDate = (dateToFormat) => {
        const year = dateToFormat.getFullYear();
        const month = String(dateToFormat.getMonth() + 1).padStart(2, "0");
        const day = String(dateToFormat.getDate()).padStart(2, "0");
        const formatedDate = `${year}-${month}-${day}`;
        return formatedDate;
    }

    const getMaterialManufacturerReport = () => {
        setLoading(true);
        const startDate = getFormattedDate(selectedStartDate);
        const endDate = getFormattedDate(selectedEndDate);
        const requestedData = {
            StartDate: startDate,
            EndDate: endDate,
            Depot: userDepot
        }

        const endPoint = SERVICE_LOCATION + GET_MATERIAL_MANUFACTURER_REPORT;
        axios.post(endPoint, requestedData, {
            headers: {
                Authorization: `Bearer ${data.data.token}`,
            },
        })
            .then((response) => {
                setLoading(false);
                if (response.data.isSuccess) {
                    const materialManufacturerData = response.data.data.materialManufacturer;
                    setMaterialManufacturerReportData(materialManufacturerData);
                    setFilteredReportData(materialManufacturerData);
                }
                else if (!response.data.isSuccess) {
                    setMaterialManufacturerReportData(null);
                    setFilteredReportData(null);
                    toast.current.show({ severity: "error", summary: "Error", detail: response.data.errorMessage, life: 3000 });
                }
            })
            .catch((error) => {
                setMaterialManufacturerReportData(null);
                setFilteredReportData(null);
                toast.current.show({ severity: "error", summary: "Error", detail: error, life: 3000 });
            })
    };

    const handleStartDateChange = (e) => {
        const dateSelected = e.value;
        setSelectedStartDate(dateSelected);
        setMinDate(dateSelected);
        setSelectedEndDate(null);
        setDisableSubmitButton(true);
    }

    const handleEndDateChange = (e) => {
        const dateSelected = e.value;
        setSelectedEndDate(dateSelected);
        setDisableSubmitButton(false);
    }

    const downloadMaterialManufacturerReport = () => {
        getMaterialManufacturerReport();
        setShowReport(true);
    }

    const getFilteredReportData = (filteredData) => {
        setFilteredReportData(filteredData);
    }

    //export functionality
    const saveAsExcelFile = (buffer, fileName) => {
        import("file-saver").then((module) => {
            if (module && module.default) {
                let EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
                let EXCEL_EXTENSION = ".xlsx";
                const data = new Blob([buffer], {
                    type: EXCEL_TYPE
                });

                module.default.saveAs(data, fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION);
            }
        });
    };

    const exportExcel = () => {
        import("xlsx").then((xlsx) => {
            const worksheet = xlsx.utils.json_to_sheet(filteredReportData);
            const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
            const excelBuffer = xlsx.write(workbook, {
                bookType: "xlsx",
                type: "array"
            });

            saveAsExcelFile(excelBuffer, "MaterialManufacturerReport");
        });
    };
    //end export functionality

    // Search functionality
    const clearFilter = () => {
        initFilters();
    };

    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            measureId: { value: null, matchMode: FilterMatchMode.CONTAINS },
            worksheetNo: { value: null, matchMode: FilterMatchMode.CONTAINS },
            materialManufacturer: { value: null, matchMode: FilterMatchMode.CONTAINS },
            jobId: { value: null, matchMode: FilterMatchMode.CONTAINS },
            siteName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            siteCity: { value: null, matchMode: FilterMatchMode.CONTAINS },
            sitePostalCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
            buildingCompany: { value: null, matchMode: FilterMatchMode.CONTAINS },
            installerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            materialName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            area: { value: null, matchMode: FilterMatchMode.CONTAINS },
            cavityWidth: { value: null, matchMode: FilterMatchMode.CONTAINS },
            totalBags: { value: null, matchMode: FilterMatchMode.CONTAINS },
            technicianName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            depotName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            buildingType: { value: null, matchMode: FilterMatchMode.CONTAINS },
            buildingForm: { value: null, matchMode: FilterMatchMode.CONTAINS },
            jobDropped: { value: null, matchMode: FilterMatchMode.CONTAINS },
            plot: { value: null, matchMode: FilterMatchMode.CONTAINS },
            submittedDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
            boxWeight: { value: null, matchMode: FilterMatchMode.CONTAINS },
            boxTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
            scannedCodes: { value: null, matchMode: FilterMatchMode.CONTAINS },
            comment: { value: null, matchMode: FilterMatchMode.CONTAINS },
            country: { value: null, matchMode: FilterMatchMode.CONTAINS }
        });
        setGlobalFilterValue("");
    }

    const renderHeader = () => {
        return (
            <div className="row">
                <div className="col-12 col-sm-3 col-md-3 col-lg-3 col-xl-3">
                    <div className="flex align-items-center justify-content-end gap-2">
                        <Button className="mar-r-10" type="button" icon="pi pi-file-excel" severity="success" onClick={exportExcel} rounded tooltip="Export Report" />
                    </div>
                </div>
                <div className="col-12 col-sm-9 col-md-9 col-lg-9 col-xl-9">
                    <div className="flex justify-content-end txt-right">
                        <Button className="mar-r-10" type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} tooltip="clear filters" tooltipOptions={{ position: "bottom" }} />
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
                        </span>
                    </div>
                </div>

            </div>
        );
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters["global"].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const header = renderHeader();
    // Search functionality Ends here

    return (
        <div className={`app ${loading ? "disabled" : ""}`}>
            {
                loading &&
                <div className="spinner-overlay">
                    <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
                </div>
            }

            <Toast ref={toast} />

            <div className="worksheets-section padl-20">
                <h4>Material Manufacturer</h4>
                <div className="row pad-tb-10">
                    <div className="col-3">
                        <label htmlFor="start-date">Start Date</label>
                        <Calendar id="start-date" name="start-date" value={selectedStartDate} onChange={handleStartDateChange} showIcon dateFormat="dd/mm/yy" />
                    </div>
                    <div className="col-3">
                        <label htmlFor="end-date">End Date</label>
                        <Calendar id="end-date" name="end-date" value={selectedEndDate} minDate={minDate} onChange={handleEndDateChange} showIcon dateFormat="dd/mm/yy" />
                    </div>
                    <div className="col-3">
                        <Button id="submit" label="Submit" disabled={disableSubmitButton} onClick={downloadMaterialManufacturerReport} />
                    </div>
                </div>

                {
                    showReport &&
                    <div className="row">
                        <div className="col-12">
                            <DataTable className="font-12" value={materialManufacturerReportData} dataKey="id" paginator rows={10}
                                rowsPerPageOptions={[10, 25, 50]} filters={filters} header={header} filterDisplay="row" onValueChange={getFilteredReportData}
                                globalFilterFields={["id", "measureId", "worksheetNo", "materialManufacturer", "jobId", "siteName", "siteCity", "sitePostalCode",
                                    "buildingCompany", "installerName", "materialName", "area", "cavityWidth", "totalBags",
                                    "technicianName", "depotName", "buildingType", "buildingForm", "jobDropped", "plot",
                                    "submittedDate", "boxWeight", "boxTime", "scannedCodes", "comment", "country"]}
                                scrollable tableStyle={{ minWidth: "100rem" }} emptyMessage="No jobs availabe for the selected date range.">
                                <Column field="id" key="id" header="Id" className="table-header" hidden></Column>
                                <Column field="measureId" header="MeasureId" filter filterPlaceholder="Search by measureId" className="table-header"></Column>
                                <Column field="worksheetNo" header="WorksheetNo" filter filterPlaceholder="Search by worksheetno" className="table-header"></Column>
                                <Column field="materialManufacturer" header="Material Manufacturer" filter filterPlaceholder="Search by material" className="table-header" ></Column>
                                <Column field="jobId" header="Job Id" filter filterPlaceholder="Search by job id" className="table-header" ></Column>
                                <Column field="siteName" header="Site Name" filter filterPlaceholder="Search by site name" className="table-header"></Column>
                                <Column field="siteCity" header="Site City" filter filterPlaceholder="Search by site city" className="table-header"></Column>
                                <Column field="sitePostalCode" header="Site Postal Code" filter filterPlaceholder="Search by site postal code" className="table-header"></Column>
                                <Column field="buildingCompany" header="Building Company" filter filterPlaceholder="Search by building company" className="table-header"></Column>
                                <Column field="installerName" header="Installer Name" filter filterPlaceholder="Search by installer name" className="table-header"></Column>
                                <Column field="materialName" header="Material Name" filter filterPlaceholder="Search by material name" className="table-header"></Column>
                                <Column field="area" header="Area" filter filterPlaceholder="Search by worksheet code" className="table-header" hidden></Column>
                                <Column field="cavityWidth" header="Cavity Width" filter filterPlaceholder="Search by cavity width" className="table-header"></Column>
                                <Column field="totalBags" header="Total Bags" filter filterPlaceholder="Search by total bags" className="table-header"></Column>
                                <Column field="technicianName" header="Technician Name" filter filterPlaceholder="Search by technician name" className="table-header"></Column>
                                <Column field="depotName" header="Depot Name" filter filterPlaceholder="Search by depot name" className="table-header"></Column>
                                <Column field="buildingType" header="Building Type" filter filterPlaceholder="Search by building type" className="table-header"></Column>
                                <Column field="buildingForm" header="Building Form" filter filterPlaceholder="Search by building form" className="table-header"></Column>
                                <Column field="jobDropped" header="Job Dropped" filter filterPlaceholder="Search by job dropped" className="table-header"></Column>
                                <Column field="plot" header="Plot No" filter filterPlaceholder="Search by plot" className="table-header"></Column>
                                <Column field="submittedDate" header="Submitted Date" filter filterPlaceholder="Search by submitted date" className="table-header"></Column>
                                <Column field="boxWeight" header="Box Weight" filter filterPlaceholder="Search by box weight" className="table-header"></Column>
                                <Column field="boxTime" header="Box Time" filter filterPlaceholder="Search by box time" className="table-header"></Column>
                                <Column field="scannedCodes" header="Scanned Codes" filter filterPlaceholder="Search by scanned codes" className="table-header"></Column>
                                <Column field="comment" header="Comment" filter filterPlaceholder="Search by comment" className="table-header"></Column>
                                <Column field="country" header="Country" filter filterPlaceholder="Search by country" className="table-header"></Column>
                            </DataTable>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

export default MaterialManufacturerReport;